import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import { MobileSelect } from '../components/ui/mobile-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

/**
 * Safari iOS Dropdown Test Page V2
 * 
 * Deze pagina test EXACT de 3 fixes die je hebt beschreven:
 * 1. Z-index + stacking context met -webkit-transform: translate3d(0,0,0)
 * 2. Overflow: visible !important check
 * 3. JavaScript Reflow Hack
 */

const options = [
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'fashion', label: 'Fashion' },
  { value: 'sports', label: 'Sports & Outdoors' },
];

// ============================================
// FIX 1: Z-Index + Stacking Context
// Exact zoals beschreven:
// position: relative; z-index: 50; -webkit-transform: translate3d(0,0,0);
// ============================================
const DropdownFix1_ZIndex = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div 
      ref={wrapperRef}
      className="dropdown-wrapper"
      style={{
        position: 'relative',
        zIndex: 50,
        WebkitTransform: 'translate3d(0,0,0)',
        transform: 'translate3d(0,0,0)',
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-4 bg-white dark:bg-gray-800 border-2 border-blue-500 rounded-xl flex items-center justify-between text-left text-foreground"
      >
        <span className="text-base">{selected ? options.find(o => o.value === selected)?.label : 'Kies een categorie...'}</span>
        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border-2 border-blue-500 rounded-xl shadow-2xl"
          style={{
            zIndex: 9999,
            WebkitTransform: 'translate3d(0,0,0)',
            transform: 'translate3d(0,0,0)',
          }}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setSelected(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-4 text-left flex items-center justify-between text-foreground
                ${index === 0 ? 'rounded-t-xl' : ''} 
                ${index === options.length - 1 ? 'rounded-b-xl' : ''}
                ${selected === option.value ? 'bg-blue-100 dark:bg-blue-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}
              `}
            >
              <span className="text-base">{option.label}</span>
              {selected === option.value && <Check className="w-5 h-5 text-blue-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// FIX 2: Overflow Visible op ALLE parent containers
// Test: overflow: visible !important; op de hele sectie
// ============================================
const DropdownFix2_OverflowVisible = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div 
      ref={wrapperRef}
      style={{
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-4 bg-white dark:bg-gray-800 border-2 border-green-500 rounded-xl flex items-center justify-between text-left text-foreground"
      >
        <span className="text-base">{selected ? options.find(o => o.value === selected)?.label : 'Kies een categorie...'}</span>
        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border-2 border-green-500 rounded-xl shadow-2xl"
          style={{
            position: 'absolute',
            overflow: 'visible',
            zIndex: 9999,
          }}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setSelected(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-4 text-left flex items-center justify-between text-foreground
                ${index === 0 ? 'rounded-t-xl' : ''} 
                ${index === options.length - 1 ? 'rounded-b-xl' : ''}
                ${selected === option.value ? 'bg-green-100 dark:bg-green-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}
              `}
            >
              <span className="text-base">{option.label}</span>
              {selected === option.value && <Check className="w-5 h-5 text-green-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// FIX 3: JavaScript Reflow Hack
// Forceer Safari om de layout te herberekenen
// document.body.style.display = 'table' / 'block'
// ============================================
const DropdownFix3_ReflowHack = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapperRef = useRef(null);
  const touchHandledRef = useRef(false);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const forceReflow = () => {
    document.body.style.display = 'table';
    setTimeout(() => {
      document.body.style.display = 'block';
    }, 0);
  };

  const handleOpen = () => {
    forceReflow();
    setIsOpen(!isOpen);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    touchHandledRef.current = true;
    handleOpen();
    setTimeout(() => { touchHandledRef.current = false; }, 300);
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (touchHandledRef.current) return;
    handleOpen();
  };

  return (
    <div 
      ref={wrapperRef}
      style={{ position: 'relative' }}
    >
      <button
        type="button"
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        className="w-full px-4 py-4 bg-white dark:bg-gray-800 border-2 border-purple-500 rounded-xl flex items-center justify-between text-left text-foreground"
        style={{
          WebkitTapHighlightColor: 'transparent',
          WebkitUserSelect: 'none',
          touchAction: 'manipulation',
        }}
      >
        <span className="text-base">{selected ? options.find(o => o.value === selected)?.label : 'Kies een categorie...'}</span>
        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border-2 border-purple-500 rounded-xl shadow-2xl z-[9999]"
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setSelected(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-4 text-left flex items-center justify-between text-foreground
                ${index === 0 ? 'rounded-t-xl' : ''} 
                ${index === options.length - 1 ? 'rounded-b-xl' : ''}
                ${selected === option.value ? 'bg-purple-100 dark:bg-purple-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}
              `}
            >
              <span className="text-base">{option.label}</span>
              {selected === option.value && <Check className="w-5 h-5 text-purple-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// FIX 4: ALLE DRIE FIXES GECOMBINEERD
// Z-index + overflow visible + reflow hack
// ============================================
const DropdownFix4_AllCombined = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const wrapperRef = useRef(null);
  const touchHandledRef = useRef(false);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const forceReflow = () => {
    document.body.style.display = 'table';
    setTimeout(() => {
      document.body.style.display = 'block';
    }, 0);
  };

  const handleOpen = () => {
    forceReflow();
    setIsOpen(!isOpen);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    touchHandledRef.current = true;
    handleOpen();
    setTimeout(() => { touchHandledRef.current = false; }, 300);
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (touchHandledRef.current) return;
    handleOpen();
  };

  return (
    <div 
      ref={wrapperRef}
      className="dropdown-wrapper"
      style={{
        position: 'relative',
        zIndex: 50,
        overflow: 'visible',
        WebkitTransform: 'translate3d(0,0,0)',
        transform: 'translate3d(0,0,0)',
      }}
    >
      <button
        type="button"
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        className="w-full px-4 py-4 bg-white dark:bg-gray-800 border-2 border-orange-500 rounded-xl flex items-center justify-between text-left text-foreground"
        style={{
          WebkitTapHighlightColor: 'transparent',
          WebkitUserSelect: 'none',
          touchAction: 'manipulation',
        }}
      >
        <span className="text-base">{selected ? options.find(o => o.value === selected)?.label : 'Kies een categorie...'}</span>
        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border-2 border-orange-500 rounded-xl shadow-2xl"
          style={{
            position: 'absolute',
            zIndex: 9999,
            overflow: 'visible',
            WebkitTransform: 'translate3d(0,0,0)',
            transform: 'translate3d(0,0,0)',
          }}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setSelected(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-4 text-left flex items-center justify-between text-foreground
                ${index === 0 ? 'rounded-t-xl' : ''} 
                ${index === options.length - 1 ? 'rounded-b-xl' : ''}
                ${selected === option.value ? 'bg-orange-100 dark:bg-orange-900/30' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}
              `}
            >
              <span className="text-base">{option.label}</span>
              {selected === option.value && <Check className="w-5 h-5 text-orange-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// FIX 5: Native HTML Select (als backup)
// Compleet kaal - geen styling, geen classes
// ============================================
const DropdownFix5_NativeSelect = () => {
  const [selected, setSelected] = useState('');

  return (
    <div>
      <p className="text-sm text-cyan-500 mb-2">100% Native select (geen styling):</p>
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        style={{ 
          width: '100%', 
          padding: '16px',
          fontSize: '16px',
          border: '2px solid cyan',
          borderRadius: '12px',
          backgroundColor: 'white',
          color: 'black',
        }}
      >
        <option value="">Kies een categorie...</option>
        <option value="vehicles">Vehicles</option>
        <option value="electronics">Electronics</option>
        <option value="real_estate">Real Estate</option>
        <option value="fashion">Fashion</option>
        <option value="sports">Sports</option>
      </select>
      {selected && (
        <p className="mt-2 text-green-500 text-sm">Geselecteerd: {selected}</p>
      )}
    </div>
  );
};

// ============================================
// FIX 6: MobileSelect Component Test
// Test de echte component die in de app wordt gebruikt
// ============================================
const MobileSelectTest = () => {
  const [selected, setSelected] = useState('');

  return (
    <MobileSelect
      options={options}
      value={selected}
      onChange={(e) => setSelected(e.target.value)}
      placeholder="Kies een categorie..."
      label="Test Category"
      data-testid="mobile-select-test"
    />
  );
};

// ============================================
// FIX 7: MobileSelect inside a FORM
// Simulates the CreateListingPage context
// ============================================
const MobileSelectInForm = () => {
  const [selected, setSelected] = useState('');

  return (
    <form onSubmit={(e) => e.preventDefault()} style={{ overflow: 'visible' }}>
      <div className="relative z-[100]" style={{ overflow: 'visible' }}>
        <label className="block text-sm font-medium text-foreground mb-1.5">Category *</label>
        <MobileSelect
          name="category"
          options={options}
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          placeholder="Select category"
          label="Select Category"
          data-testid="mobile-select-form-test"
          className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm"
        />
      </div>
    </form>
  );
};

// ============================================
// FIX 8: MobileSelect with Sticky Header
// Simulates the CreateListingPage sticky header
// ============================================
const MobileSelectWithStickyHeader = () => {
  const [selected, setSelected] = useState('');
  const [headerDropdownOpen, setHeaderDropdownOpen] = useState(false);

  return (
    <div className="relative">
      {/* Simulated sticky header like CreateListingPage */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 mb-4 -mx-5 -mt-5 rounded-t-2xl">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">← Back</span>
          
          {/* Step Dropdown like CreateListingPage */}
          <div className="relative">
            <button
              onClick={() => setHeaderDropdownOpen(!headerDropdownOpen)}
              className="flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-xl text-sm font-medium"
            >
              Step 1 of 4
              <ChevronDown className={`w-4 h-4 transition-transform ${headerDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {headerDropdownOpen && (
              <>
                {/* This overlay blocks everything! */}
                <div 
                  className="fixed inset-0 z-[9998]" 
                  onClick={() => setHeaderDropdownOpen(false)}
                />
                <div className="absolute top-full right-0 mt-2 w-48 bg-card rounded-xl border border-border shadow-lg z-[9999]">
                  <button className="w-full px-4 py-2.5 text-sm text-left hover:bg-secondary/50 rounded-t-xl">Step 1</button>
                  <button className="w-full px-4 py-2.5 text-sm text-left hover:bg-secondary/50">Step 2</button>
                  <button className="w-full px-4 py-2.5 text-sm text-left hover:bg-secondary/50 rounded-b-xl">Step 3</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* The MobileSelect */}
      <form onSubmit={(e) => e.preventDefault()} style={{ overflow: 'visible' }}>
        <div className="relative z-[100]" style={{ overflow: 'visible' }}>
          <label className="block text-sm font-medium text-foreground mb-1.5">Category *</label>
          <MobileSelect
            name="category"
            options={options}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            placeholder="Select category"
            label="Select Category"
            data-testid="mobile-select-sticky-test"
            className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm"
          />
        </div>
      </form>
    </div>
  );
};

// ============================================
// FIX 9: MobileSelect with Dialog component
// Simulates the Dialog components in CreateListingPage
// ============================================
const MobileSelectWithDialog = () => {
  const [selected, setSelected] = useState('');
  const [showDialog, setShowDialog] = useState(false);

  return (
    <div className="relative">
      {/* Button to open dialog */}
      <button
        onClick={() => setShowDialog(true)}
        className="mb-4 px-4 py-2 bg-indigo-500 text-white rounded-xl text-sm"
      >
        Open Dialog (dan sluiten en dropdown testen)
      </button>

      {/* Dialog like CreateListingPage has */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Dialog</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Sluit deze dialog en test dan de dropdown hieronder.
          </p>
          <button
            onClick={() => setShowDialog(false)}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-xl text-sm"
          >
            Sluiten
          </button>
        </DialogContent>
      </Dialog>

      {/* The MobileSelect */}
      <form onSubmit={(e) => e.preventDefault()} style={{ overflow: 'visible' }}>
        <div className="relative z-[100]" style={{ overflow: 'visible' }}>
          <label className="block text-sm font-medium text-foreground mb-1.5">Category *</label>
          <MobileSelect
            name="category"
            options={options}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            placeholder="Select category"
            label="Select Category"
            data-testid="mobile-select-dialog-test"
            className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm"
          />
        </div>
      </form>
    </div>
  );
};

// ============================================
// FIX 10: FULL CreateListingPage Simulation
// Everything together: Dialog + Sticky Header + Steps + Form
// ============================================
const FullCreateListingSimulation = () => {
  const [selected, setSelected] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [showDraftDialog, setShowDraftDialog] = useState(true);
  const [listingType, setListingType] = useState('');
  const [headerDropdownOpen, setHeaderDropdownOpen] = useState(false);

  const goToStep1 = () => {
    if (listingType) {
      setCurrentStep(1);
    }
  };

  return (
    <div className="relative -mx-5 -mt-5 -mb-5">
      {/* Draft Dialog - shows on mount like CreateListingPage */}
      <Dialog open={showDraftDialog} onOpenChange={setShowDraftDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Continue Draft?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Je hebt een concept listing. Wil je doorgaan?
          </p>
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setShowDraftDialog(false)}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-xl text-sm"
            >
              Start New
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">← Back</span>
          
          <div className="relative">
            <button
              onClick={() => setHeaderDropdownOpen(!headerDropdownOpen)}
              className="flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-xl text-sm font-medium"
            >
              Step {currentStep} of 4
              <ChevronDown className={`w-4 h-4 transition-transform ${headerDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {headerDropdownOpen && (
              <>
                <div 
                  className="fixed inset-0 z-[9998]" 
                  onClick={() => setHeaderDropdownOpen(false)}
                />
                <div className="absolute top-full right-0 mt-2 w-48 bg-card rounded-xl border border-border shadow-lg z-[9999]">
                  <button onClick={() => { setCurrentStep(0); setHeaderDropdownOpen(false); }} className="w-full px-4 py-2.5 text-sm text-left hover:bg-secondary/50 rounded-t-xl">Step 0: Type</button>
                  <button onClick={() => { setCurrentStep(1); setHeaderDropdownOpen(false); }} className="w-full px-4 py-2.5 text-sm text-left hover:bg-secondary/50 rounded-b-xl">Step 1: Story</button>
                </div>
              </>
            )}
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="flex gap-1 mt-2">
          {[0, 1, 2, 3].map((step) => (
            <div
              key={step}
              className={`flex-1 h-1.5 rounded-full ${step <= currentStep ? 'bg-primary' : 'bg-muted'}`}
            />
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="p-5">
        <form onSubmit={(e) => e.preventDefault()} style={{ overflow: 'visible' }}>
          
          {/* Step 0: Type Selection */}
          {currentStep === 0 && (
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">What would you like to do?</h2>
              <div className="grid grid-cols-2 gap-3">
                {['sell', 'auction', 'trade', 'rent'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setListingType(type)}
                    className={`p-4 rounded-xl border-2 text-left capitalize ${
                      listingType === type ? 'border-primary bg-primary/10' : 'border-border'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={goToStep1}
                disabled={!listingType}
                className="mt-4 w-full px-4 py-3 bg-primary text-white rounded-xl disabled:opacity-50"
              >
                Next →
              </button>
            </div>
          )}

          {/* Step 1: The Story (with Category dropdown) */}
          {currentStep === 1 && (
            <div className="space-y-4" style={{ overflow: 'visible' }}>
              <h2 className="text-xl font-bold text-foreground mb-4">Tell the Story</h2>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Title *</label>
                <input
                  type="text"
                  placeholder="e.g., 2020 Toyota Corolla"
                  className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm"
                />
              </div>
              
              {/* THE CATEGORY DROPDOWN */}
              <div className="relative z-[100]" style={{ overflow: 'visible' }}>
                <label className="block text-sm font-medium text-foreground mb-1.5">Category *</label>
                <MobileSelect
                  name="category"
                  options={options}
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  placeholder="Select category"
                  label="Select Category"
                  data-testid="mobile-select-full-sim"
                  className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm"
                />
              </div>
              
              <button
                type="button"
                onClick={() => setCurrentStep(0)}
                className="mt-4 px-4 py-2 border border-border rounded-xl text-sm"
              >
                ← Back
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

// ============================================
// Main Test Page
// ============================================
const SafariDropdownTestV2 = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Force overflow visible on all containers */}
      <style>{`
        .test-section {
          overflow: visible !important;
        }
        .test-card {
          overflow: visible !important;
        }
      `}</style>
      
      <div 
        className="max-w-xl mx-auto px-4 py-8 pt-24 test-section"
        style={{ overflow: 'visible' }}
      >
        <h1 className="text-2xl font-bold text-foreground mb-2">Safari Dropdown Test V2</h1>
        <p className="text-muted-foreground mb-6 text-sm">
          Test deze dropdowns op je iOS Safari. Rapporteer welke werkt!
        </p>
        
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              <strong className="text-yellow-600 dark:text-yellow-400">Instructies:</strong>
              <ol className="mt-2 space-y-1 list-decimal list-inside">
                <li>Tap op elke dropdown</li>
                <li>Kijk of de opties verschijnen</li>
                <li>Selecteer een optie</li>
                <li>Noteer welke fix werkt!</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="space-y-8" style={{ overflow: 'visible' }}>
          
          {/* Fix 1 */}
          <div className="test-card p-5 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-blue-500">Fix 1: Z-Index + Stacking Context</h2>
              <p className="text-xs text-muted-foreground mt-1">
                position: relative; z-index: 50; -webkit-transform: translate3d(0,0,0);
              </p>
            </div>
            <DropdownFix1_ZIndex />
          </div>

          {/* Fix 2 */}
          <div className="test-card p-5 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-green-500">Fix 2: Overflow Visible</h2>
              <p className="text-xs text-muted-foreground mt-1">
                overflow: visible !important; op alle parent containers
              </p>
            </div>
            <DropdownFix2_OverflowVisible />
          </div>

          {/* Fix 3 */}
          <div className="test-card p-5 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-purple-500">Fix 3: JavaScript Reflow Hack</h2>
              <p className="text-xs text-muted-foreground mt-1">
                document.body.style.display = 'table' / 'block'
              </p>
            </div>
            <DropdownFix3_ReflowHack />
          </div>

          {/* Fix 4 - Combined */}
          <div className="test-card p-5 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-orange-500">Fix 4: ALLES GECOMBINEERD</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Z-index + Overflow visible + Reflow hack samen
              </p>
            </div>
            <DropdownFix4_AllCombined />
          </div>

          {/* Fix 5 - Native */}
          <div className="test-card p-5 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-cyan-500">Fix 5: Native HTML Select</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Gebruikt iOS native picker (backup optie)
              </p>
            </div>
            <DropdownFix5_NativeSelect />
          </div>

          {/* Fix 6 - MobileSelect Component */}
          <div className="test-card p-5 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-red-500">Fix 6: MobileSelect Component</h2>
              <p className="text-xs text-muted-foreground mt-1">
                De echte component die in CreateListingPage wordt gebruikt
              </p>
            </div>
            <MobileSelectTest />
          </div>

          {/* Fix 7 - MobileSelect inside a FORM (like CreateListingPage) */}
          <div className="test-card p-5 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-pink-500">Fix 7: MobileSelect in FORM</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Test of de form-wrapper het probleem veroorzaakt
              </p>
            </div>
            <MobileSelectInForm />
          </div>

          {/* Fix 8 - MobileSelect with STICKY HEADER above it */}
          <div className="test-card p-5 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-yellow-500">Fix 8: Met STICKY HEADER</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Simuleert de sticky header van CreateListingPage
              </p>
            </div>
            <MobileSelectWithStickyHeader />
          </div>

          {/* Fix 9 - MobileSelect with DIALOG on page */}
          <div className="test-card p-5 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-indigo-500">Fix 9: Met DIALOG component</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Simuleert de Dialog componenten van CreateListingPage
              </p>
            </div>
            <MobileSelectWithDialog />
          </div>

          {/* Fix 10 - FULL CreateListingPage simulation */}
          <div className="test-card p-5 bg-card border border-border rounded-2xl" style={{ overflow: 'visible' }}>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-emerald-500">Fix 10: VOLLEDIGE CreateListingPage simulatie</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Alles samen: Dialog + Sticky Header + Step Navigation + Form
              </p>
            </div>
            <FullCreateListingSimulation />
          </div>

        </div>

        <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
          <p className="text-sm text-muted-foreground">
            <strong className="text-blue-500">Feedback:</strong> Laat me weten welke fix(es) werken op je iPhone Safari, 
            dan pas ik die toe op de echte dropdowns in de app!
          </p>
        </div>
      </div>
    </div>
  );
};

export default SafariDropdownTestV2;
